import type {
  ContractApprovalSummary,
  ContractReconciliationSummary,
  ContractReviewSummary,
  ContractSignatureRequestSummary,
} from "@closer/shared";
import {
  CONTRACT_APPROVAL_STATUS_NAMES,
  CONTRACT_RECONCILIATION_STATUS_NAMES,
  CONTRACT_REVIEW_STATUS_NAMES,
  CONTRACT_SIGNATURE_REQUEST_STATUS_NAMES,
} from "@closer/shared";
import { Badge, Skeleton } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";

import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { relativeDateLabel } from "../../lib/relativeDate";
import { tenantKey } from "../../lib/tenantQuery";

/**
 * KRİTİK SÜREÇLER ÖZETİ (CLOS-1112 · rapor 5 §3, UAT #40).
 *
 * ### ⚠ Cevapladığı TEK soru
 *
 * Rapor: *"Sözleşme detayının görevi: 'Güncel versiyonun kritik süreçleri şu anda ne
 * durumda?' sorusunu cevaplamaktır."* Dört süreç dört ayrı bölümde yaşıyordu ve
 * kullanıcı durumu görmek için dördünü tek tek açmak zorundaydı.
 *
 * ### ⚠ BURADA KARAR VERİLMEZ
 *
 * Rapor aynı cümlede sınırı da çiziyor: *"Kritik kararlar bu özet panelinde verilmez."*
 * Panel bilerek SALT OKUNUR — düğme yok, mutasyon yok. Karar, sürecin kendi bölümünde ve
 * değişmez versiyon bağlamında veriliyor. Özet paneline aksiyon koymak, aynı kararın iki
 * yerde verilebilmesi demekti ve ikisi ayrışırdı.
 *
 * ### ⚠ AYNI SORGU ANAHTARLARI — ek ağ isteği YOK
 *
 * Dört sorgu, bölümlerin kullandığı anahtarların BİREBİR aynısı. React Query önbelleği
 * paylaşıldığı için panel veriyi ikinci kez çekmiyor; bölüm bir karar sonrası anahtarı
 * tazelediğinde panel de kendiliğinden güncelleniyor. Kendi anahtarımızı üretseydik iki
 * kopya doğar ve biri bayat kalırdı.
 *
 * ### ⚠ GÜNCEL VERSİYON ÜZERİNDEN
 *
 * Her süreç DEĞİŞMEZ bir versiyona çakılı. Panel yalnız GÜNCEL versiyonun akışlarını
 * sayıyor; eski versiyonların kararları geçmişte kalır ve yeni versiyona taşınmaz
 * (`approvals.ts`: yeni versiyon açık akışı `superseded` yapıyor). Hepsini karıştırsaydık
 * "onaylandı" diyen bir panel, aslında iki versiyon önce onaylanmış bir metni gösterirdi.
 */

type SurecDurumu = {
  ad: string;
  durum: string;
  baslamis: boolean;
  ilerleme: string | null;
  sonTarih: string | null;
  /**
   * Son tarihin GÖRELİ hâli ("5 gün kaldı", "3 gün önce").
   *
   * ⚠ Mutlak tarihin yerine geçmiyor, yanına geliyor: kullanıcı günün kendisini takvime
   * işliyor, aciliyeti ise okumak istiyor. Panelin cevapladığı soru ("süreçler ne
   * durumda?") aciliyet olmadan yarım kalıyordu — "Son tarih 5 Eyl 2026" satırı, tarihin
   * geçip geçmediğini kullanıcının kafasında hesaplamasını gerektiriyordu.
   */
  sonTarihGoreli: string | null;
};

function tarih(iso: string | null): string | null {
  return iso ? new Date(iso).toLocaleDateString("tr-TR", { dateStyle: "medium" }) : null;
}

/** Güncel versiyona ait EN SON akış; yoksa `null`. */
function guncelAkis<T extends { versionNumber: number; createdAt: string }>(
  akislar: T[] | undefined,
  versiyon: number | null,
): T | null {
  if (!akislar || versiyon === null) return null;
  const uygun = akislar.filter((a) => a.versionNumber === versiyon);
  if (uygun.length === 0) return null;
  return [...uygun].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]!;
}

export function ContractCriticalProcesses({ contractId }: { contractId: string }) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;

  /** ⚠ Anahtarlar bölümlerinkiyle AYNI — önbellek paylaşılıyor, ikinci istek gitmiyor. */
  const incelemeler = useQuery({
    queryKey: tenantKey(tenantId, "contract", contractId, "incelemeler"),
    queryFn: async () =>
      (await api<ContractReviewSummary[]>(`/api/contracts/${contractId}/reviews`, { tenantId }))
        .data,
  });
  const onaylar = useQuery({
    queryKey: tenantKey(tenantId, "contract", contractId, "onaylar"),
    queryFn: async () =>
      (await api<ContractApprovalSummary[]>(`/api/contracts/${contractId}/approvals`, { tenantId }))
        .data,
  });
  const mutabakatlar = useQuery({
    queryKey: tenantKey(tenantId, "contract", contractId, "mutabakatlar"),
    queryFn: async () =>
      (
        await api<ContractReconciliationSummary[]>(`/api/contracts/${contractId}/reconciliations`, {
          tenantId,
        })
      ).data,
  });
  const imzalar = useQuery({
    queryKey: tenantKey(tenantId, "contract", contractId, "imza"),
    queryFn: async () =>
      (
        await api<ContractSignatureRequestSummary[]>(
          `/api/contracts/${contractId}/signature-requests`,
          { tenantId },
        )
      ).data,
  });

  const yukleniyor =
    incelemeler.isPending || onaylar.isPending || mutabakatlar.isPending || imzalar.isPending;

  /**
   * ⚠ GÜNCEL VERSİYON AKIŞLARDAN TÜRETİLİYOR, ayrı bir istek atılmıyor. Dört listedeki en
   * yüksek sürüm numarası, üzerinde iş yapılmış en yeni versiyondur. Versiyon listesini
   * ayrıca çekmek beşinci bir istek olurdu ve panelin cevapladığı soru için gereksiz.
   */
  const versiyon = Math.max(
    0,
    ...(incelemeler.data ?? []).map((a) => a.versionNumber),
    ...(onaylar.data ?? []).map((a) => a.versionNumber),
    ...(mutabakatlar.data ?? []).map((a) => a.versionNumber),
    ...(imzalar.data ?? []).map((a) => a.versionNumber),
  );
  const guncelVersiyon = versiyon > 0 ? versiyon : null;

  const inceleme = guncelAkis(incelemeler.data, guncelVersiyon);
  const onay = guncelAkis(onaylar.data, guncelVersiyon);
  const mutabakat = guncelAkis(mutabakatlar.data, guncelVersiyon);
  const imza = guncelAkis(imzalar.data, guncelVersiyon);

  const surecler: SurecDurumu[] = [
    {
      ad: "İnceleme",
      durum: inceleme ? CONTRACT_REVIEW_STATUS_NAMES[inceleme.status] : "Başlatılmadı",
      baslamis: inceleme !== null,
      ilerleme: inceleme
        ? `${inceleme.tasks.filter((g) => g.status !== "pending").length} / ${inceleme.tasks.length} inceleyici`
        : null,
      sonTarih: tarih(inceleme?.dueDate ?? null),
      sonTarihGoreli: relativeDateLabel(inceleme?.dueDate ?? null),
    },
    {
      ad: "İç Onay",
      durum: onay ? CONTRACT_APPROVAL_STATUS_NAMES[onay.status] : "Başlatılmadı",
      baslamis: onay !== null,
      ilerleme: onay
        ? (() => {
            const gorevler = onay.stages.flatMap((a) => a.tasks);
            return `${gorevler.filter((g) => g.status !== "pending" && g.status !== "blocked").length} / ${gorevler.length} onay`;
          })()
        : null,
      sonTarih: tarih(onay?.dueDate ?? null),
      sonTarihGoreli: relativeDateLabel(onay?.dueDate ?? null),
    },
    {
      ad: "Mutabakat",
      durum: mutabakat ? CONTRACT_RECONCILIATION_STATUS_NAMES[mutabakat.status] : "Başlatılmadı",
      baslamis: mutabakat !== null,
      ilerleme: mutabakat
        ? `${mutabakat.tasks.filter((g) => g.status !== "pending").length} / ${mutabakat.tasks.length} taraf`
        : null,
      sonTarih: tarih(mutabakat?.dueDate ?? null),
      sonTarihGoreli: relativeDateLabel(mutabakat?.dueDate ?? null),
    },
    {
      ad: "İmza",
      durum: imza ? CONTRACT_SIGNATURE_REQUEST_STATUS_NAMES[imza.status] : "Başlatılmadı",
      baslamis: imza !== null,
      ilerleme: imza
        ? `${imza.signers.filter((i) => i.status === "signed").length} / ${imza.signers.length} imza`
        : null,
      sonTarih: tarih(imza?.expiresAt ?? null),
      sonTarihGoreli: relativeDateLabel(imza?.expiresAt ?? null),
    },
  ];

  return (
    <section className="flex flex-col gap-2" data-testid="kritik-surecler">
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 className="text-base font-semibold text-fg-heading">Kritik Süreçler</h2>
        {guncelVersiyon !== null ? (
          <span className="text-xs text-fg-soft">Güncel sürüm: {guncelVersiyon}</span>
        ) : null}
      </div>

      {yukleniyor ? (
        <Skeleton lines={4} />
      ) : guncelVersiyon === null ? (
        /**
         * ⚠ BOŞ HÂL HATA DEĞİL (rapor 4 §21.2): hiçbir süreç başlamamış bir sözleşme
         * normaldir. "Yüklenemedi" gibi görünmemeli.
         */
        <p className="text-sm text-fg-muted">
          Bu sözleşmede henüz inceleme, onay, mutabakat veya imza süreci başlatılmadı.
        </p>
      ) : (
        <ul className="flex flex-col">
          {surecler.map((s) => (
            <li
              key={s.ad}
              className="flex flex-wrap items-center justify-between gap-2 border-b border-border-default py-1.5 last:border-0"
            >
              <span className="text-sm text-fg">{s.ad}</span>
              <span className="flex flex-wrap items-center gap-2">
                {/* ⚠ Durum METİN olarak rozette; yalnız renkle anlatılmıyor (rapor 4 §21). */}
                <Badge tone={s.baslamis ? "warning" : "neutral"}>{s.durum}</Badge>
                <span className="text-xs text-fg-soft">
                  {[
                    s.ilerleme,
                    s.sonTarih
                      ? `Son tarih ${s.sonTarih}${s.sonTarihGoreli ? ` · ${s.sonTarihGoreli}` : ""}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
